import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ApiError, api } from '../api/client';

type AdminUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  approved_at: string | null;
};

type Props = {
  /** Single-archive transfer. Mutually exclusive with archiveIds. */
  archiveId?: number;
  /** Bulk transfer. Mutually exclusive with archiveId. */
  archiveIds?: number[];
  /** Header subtitle — domain for single, "N archives" for bulk. */
  domain: string;
  /** Current owner — hidden from the suggestions list. Single mode only. */
  currentOwnerId?: number | null;
  onClose: () => void;
  onTransferred: () => void;
};

const MAX_SUGGESTIONS = 8;

export default function TransferOwnerDialog({
  archiveId, archiveIds, domain, currentOwnerId, onClose, onTransferred,
}: Props) {
  const isBulk = archiveIds !== undefined;
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api<{ data: AdminUser[] }>('/admin/users')
      .then(({ data }) => setUsers(data))
      .catch(() => setError('Could not load users.'));
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Esc closes the dialog. Listener on window so it works even if focus
  // has slipped off the input (e.g. user clicked a non-focusable element).
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const suggestions = useMemo(() => {
    if (!users) return [];
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => u.id !== currentOwnerId)
      // Only approved users can own archives — matches the backend rule.
      .filter((u) => u.approved_at !== null)
      .filter((u) => {
        if (q === '') return true;
        return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [users, query, currentOwnerId]);

  // Reset highlight whenever the filtered list changes so we never point
  // past the end of it.
  useEffect(() => {
    setHighlight(0);
  }, [suggestions.length]);

  async function submit(user: AdminUser) {
    setBusy(true);
    setError(null);
    try {
      if (isBulk) {
        const { data } = await api<{ data: { changed: number; skipped: number } }>(
          '/archives/bulk/transfer-owner',
          { method: 'POST', body: { ids: archiveIds, user_id: user.id } },
        );
        if (data.skipped > 0) {
          window.alert(`Transferred ${data.changed}; skipped ${data.skipped} already owned by ${user.name}.`);
        }
      } else {
        await api(`/archives/${archiveId}/transfer-owner`, {
          method: 'POST',
          body: { user_id: user.id },
        });
      }
      onTransferred();
      onClose();
    } catch (e) {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object'
        ? (((e.payload as { errors?: Record<string, string[]> }).errors?.user_id?.[0])
            ?? (e.payload as { message?: string }).message
            ?? 'Transfer failed.')
        : 'Transfer failed.';
      setError(msg);
      setBusy(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = suggestions[highlight];
      if (picked && !busy) submit(picked);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '10vh', zIndex: 100,
      }}
    >
      <div
        role="dialog"
        aria-label={`Transfer ownership of ${domain}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 6, padding: 16, minWidth: 360, maxWidth: 480,
          boxShadow: 'var(--shadow-tooltip)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 4 }}>Transfer ownership</h3>
        <p className="muted" style={{ marginTop: 0, marginBottom: 12, fontSize: 13, wordBreak: 'break-word' }}>
          {domain}
        </p>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          placeholder="Search by name or email…"
          autoComplete="off"
          disabled={users === null || busy}
          style={{ width: '100%', padding: '6px 8px', boxSizing: 'border-box' }}
        />
        <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto' }}>
          {users === null ? (
            <p className="muted" style={{ fontSize: 13 }}>Loading users…</p>
          ) : suggestions.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>No matching approved users.</p>
          ) : (
            <ul role="listbox" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {suggestions.map((u, i) => (
                <li
                  key={u.id}
                  role="option"
                  aria-selected={i === highlight}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => { e.preventDefault(); if (!busy) submit(u); }}
                  style={{
                    padding: '6px 8px', cursor: 'pointer', borderRadius: 4,
                    background: i === highlight ? 'var(--pill-bg-tag)' : 'transparent',
                  }}
                >
                  <div>
                    {u.name}
                    {u.role === 'admin' && (
                      <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>admin</span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>{u.email}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {error && <p className="error" style={{ marginTop: 8, fontSize: 13 }}>{error}</p>}
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
